import { api } from './index'
import type {
  MakeGoodOffer,
  MakeGoodOfferEnvelopeWire,
  MakeGoodOffersWire,
  MakeGoodOfferWire,
} from './makeGoodLadder.types'

// Pure wire→domain mappers, exported for direct unit testing (mirrors
// escalationContacts' transformEscalationContacts). Fail-soft by construction
// (CONTRACT.md §3): a null / malformed / non-array list body coerces to []
// (never throws — a throw would become a rejected query, not empty data), a
// missing declined/materialized normalizes to false (opt-out default), and a
// missing/non-numeric up_to_cents/sort_order normalizes to 0 so a
// contract-violating row degrades to "$0" / unordered rather than rendering
// "$NaN" or breaking the sort comparator (CONTRACT.md §5.8 guarantees a
// positive whole-dollar cap, so this only bites a malformed response).
export const transformMakeGoodOffer = (
  offer: MakeGoodOfferWire
): MakeGoodOffer => ({
  id: offer.id,
  sourceTierKey: offer.source_tier_key,
  name: offer.name,
  upToCents: Number(offer.up_to_cents) || 0,
  sortOrder: Number(offer.sort_order) || 0,
  declined: Boolean(offer.declined),
  materialized: Boolean(offer.materialized),
})

export const transformMakeGoodOffers = (
  response: MakeGoodOffersWire
): MakeGoodOffer[] =>
  Array.isArray(response?.make_good_offers)
    ? response.make_good_offers.map(transformMakeGoodOffer)
    : []

// FE consumer of the recovery make_good_offers curation triad (CONTRACT.md §1),
// unconditional (AI-330/AI-383). Every read/mutation is fail-soft: consumers
// default `data` to [] and MUST keep this query's isLoading/isError out of any
// page-level gate so a 404/501 degrades to an empty ladder, never a broken pane
// (CONTRACT.md §3). Mutations touch ONLY the isolated `RecoveryMakeGoodLadder`
// tag — never RecoveryConfig / RecoveryAvailableRewards (CONTRACT.md §5.6).
export const makeGoodLadderApi = api.injectEndpoints({
  endpoints: builder => ({
    getMakeGoodOffers: builder.query<MakeGoodOffer[], void>({
      query: () => 'recovery/make_good_offers',
      transformResponse: transformMakeGoodOffers,
      providesTags: ['RecoveryMakeGoodLadder'],
    }),

    // Ladder × — declines a proffered offer (sets declined server-side). Patches
    // only the single returned row's `declined` on success rather than blindly
    // invalidating the list, so a follow-up refetch can't 404 mid-deploy and
    // blank the whole ladder (CONTRACT.md §3.6). Fails soft: on error the cache
    // is left untouched, so the UI never shows a decline that didn't persist.
    declineMakeGood: builder.mutation<MakeGoodOffer, { id: string }>({
      query: ({ id }) => ({
        url: `recovery/make_good_offers/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: MakeGoodOfferEnvelopeWire) =>
        transformMakeGoodOffer(response.make_good_offer),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: updated } = await queryFulfilled
          dispatch(
            makeGoodLadderApi.util.updateQueryData(
              'getMakeGoodOffers',
              undefined,
              draft => {
                const row = draft.find(offer => offer.id === id)
                if (row) {
                  row.declined = updated.declined
                }
              }
            )
          )
        } catch {
          // Fail soft: leave the cache unchanged (CONTRACT.md §3).
        }
      },
    }),

    // Regenerate — restores declined defaults + surfaces newly-added tiers
    // (CONTRACT.md §1.iii). Replaces the list via tag invalidation.
    regenerateMakeGood: builder.mutation<MakeGoodOffer[], void>({
      query: () => ({
        url: 'recovery/make_good_offers/regenerate',
        method: 'POST',
      }),
      transformResponse: transformMakeGoodOffers,
      invalidatesTags: ['RecoveryMakeGoodLadder'],
    }),
  }),
})

export const {
  useGetMakeGoodOffersQuery,
  useDeclineMakeGoodMutation,
  useRegenerateMakeGoodMutation,
} = makeGoodLadderApi
